/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import DocsSearchService, { Result } from 'cio/services/docs-search';
import { task, timeout } from 'ember-concurrency';
import { action } from '@ember/object';
import config from 'cio/config/environment';

interface DocsSearchArgs {
  onInput?: (value: string) => unknown;
  onUpdateResults?: (results: DisplayResult[], time: number) => unknown;
}

const MIN_TERM_LENGTH = 3;

const SECTION_ICON_MAP = {
  journeys: 'draft',
  community: 'tip',
};

type DisplayResult = Result & { icon: string };
export default class DocsSearch extends Component<DocsSearchArgs> {
  @service docsSearch!: DocsSearchService;

  @tracked results: DisplayResult[] = [];
  @tracked searchTerm = '';
  @tracked isLoading = false;

  @action
  handleInput(value: string) {
    this.searchTerm = value;
    this.args.onInput?.(value);
    this.updateSearch.perform(value);
  }

  // Only display a spinner if some time has passed since the last key press.
  // This is to prevent the spinner from blinking incessantly while the user is typing.
  showSpinner = task({ keepLatest: true }, async ({ immediate = false } = {}) => {
    await timeout(immediate === true ? 1 : config.APP.docsSearchSpinnerDisplayDelay);
    this.isLoading = true;
  });

  updateSearch = task({ keepLatest: true }, async (value: string) => {
    if (value.length === 0 && this.results.length > 0) {
      this.results = [];
      return;
    }
    if (value.length < MIN_TERM_LENGTH) {
      return;
    }

    let start = performance.now();
    let spinnerTask = this.showSpinner;
    spinnerTask.perform({ immediate: this.results.length === 0 });

    let results = await this.docsSearch.search.linked().perform(value);

    spinnerTask.cancelAll();
    this.isLoading = false;

    this.results = results.map((result) => ({
      ...result,
      icon: SECTION_ICON_MAP[result.section] ?? 'draft',
    }));

    let duration = Math.ceil(performance.now() - start);

    this.args.onUpdateResults?.(this.results, duration);
  });

  get noResultsFound() {
    return this.results.length === 0 && this.searchTerm.length >= MIN_TERM_LENGTH && this.updateSearch.isIdle;
  }

  get shouldDisplaySearchResults() {
    return this.results.length > 0 && this.searchTerm.length > 0;
  }
}
